import React, { useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShow(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don’t match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Use it next time you sign in.");
      reset();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  const handleOpen = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent
        className="max-w-md"
        style={{ backgroundColor: "var(--bg-primary)" }}
        data-testid="change-password-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <KeyRound size={20} style={{ color: "var(--brand-sage)" }} />
            <DialogTitle
              className="font-display text-2xl"
              style={{ color: "var(--text-primary)" }}
            >
              Change Password
            </DialogTitle>
          </div>
          <DialogDescription style={{ color: "var(--text-secondary)" }}>
            Pick something at least 8 characters long. You’ll stay signed in.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="change-password-form">
          <div>
            <label className="text-sm font-semibold block mb-1.5">Current password</label>
            <input
              type={show ? "text" : "password"}
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl outline-none"
              style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border-soft)" }}
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="text-sm font-semibold block mb-1.5">New password</label>
            <input
              type={show ? "text" : "password"}
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl outline-none"
              style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border-soft)" }}
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="text-sm font-semibold block mb-1.5">Confirm new password</label>
            <input
              type={show ? "text" : "password"}
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl outline-none"
              style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border-soft)" }}
              data-testid="confirm-password-input"
            />
          </div>

          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="text-xs inline-flex items-center gap-1.5 hover:underline"
            style={{ color: "var(--text-secondary)" }}
            data-testid="toggle-show-password"
          >
            {show ? <EyeOff size={12} /> : <Eye size={12} />}
            {show ? "Hide" : "Show"} passwords
          </button>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => handleOpen(false)}
              className="flex-1 py-2.5 rounded-full text-sm font-semibold"
              style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }}
              data-testid="cancel-change-password-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-sage flex-1 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
              data-testid="submit-change-password-button"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
